/**
 * A sheet's viewport outline and its matchlines, which are the same line.
 *
 * The outline is the sheet's own area grown by the overlap, so the wall at a cut shows on both
 * sheets. Where it borders a neighbouring sheet it is a cut, and a cut steps around rooms. The
 * cut is redrawn to follow the walls it runs along (square where they are square, 45 degrees only
 * where they are) with as few bends as it can, never into this sheet's own area. Each sheet then
 * draws the cut on its own viewport edge as its matchline, labelled at each outside end with both
 * sheet numbers.
 */

import type { CellPoint, Mask } from "./grid.ts";
import { dropCollinear } from "./grid.ts";
import type { Raster } from "./footprint.ts";
import { toModel } from "./footprint.ts";
import type { MatchlineLabel, PlanPoint, PlannedMatchline } from "./types.ts";
import type { DiagonalCells } from "./trace.ts";
import { traceAlongWalls } from "./trace.ts";

export interface ClipOptions {
  /** How far a matchline runs past the building at an outside end. */
  readonly extensionInches: number;
  /** Shorter cuts are corners of the outline, not matchlines. */
  readonly minLengthInches: number;
  /** Gap between the line and each label. */
  readonly labelOffsetInches: number;
  /** How much of each end of a cut stays square and untouched by the wall-following trace. */
  readonly stubInches: number;
  /**
   * How far past the overlap the line may stray into the neighbouring sheet, which is the slack it
   * has to straighten out small steps in the cut.
   */
  readonly bandInches: number;
  /** What a change of direction costs, in inches of line: higher means fewer, longer runs. */
  readonly bendInches: number;
  /** How far from a cut's end a junction may be and still be the point the cut ends at. */
  readonly junctionReachInches: number;
}

/** A point where three or more sheets of one building meet. Cell-corner coordinates. */
export interface Junction {
  readonly point: CellPoint;
  readonly sheets: ReadonlySet<number>;
}

/**
 * Every point where three or more sheets meet. The areas need not touch exactly: a notch of
 * outside between them still makes a junction, so this works on 2' blocks and counts the sheets
 * present within one block either way, then gathers neighbouring hits into one point.
 */
export function findJunctions(
  shape: { readonly width: number; readonly height: number },
  labels: Int32Array,
  buildingOf: readonly number[],
  clusterCells: number,
  mergeCells: number,
  blockCells = 4,
): Junction[] {
  const { width, height } = shape;
  const bw = Math.ceil(width / blockCells);
  const bh = Math.ceil(height / blockCells);
  const present: Set<number>[] = Array.from({ length: bw * bh }, () => new Set<number>());
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const label = labels[y * width + x]!;
      if (label >= 0) present[Math.floor(y / blockCells) * bw + Math.floor(x / blockCells)]!.add(label);
    }
  }
  const clusters: { sx: number; sy: number; n: number; sheets: Set<number>; building: number }[] = [];
  const seen = new Set<number>();
  for (let by = 0; by < bh; by++) {
    for (let bx = 0; bx < bw; bx++) {
      seen.clear();
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = bx + dx;
          const ny = by + dy;
          if (nx < 0 || ny < 0 || nx >= bw || ny >= bh) continue;
          for (const label of present[ny * bw + nx]!) seen.add(label);
        }
      }
      if (seen.size < 3) continue;
      const x = (bx + 0.5) * blockCells;
      const y = (by + 0.5) * blockCells;
      const byBuilding = new Map<number, number[]>();
      for (const label of seen) {
        const list = byBuilding.get(buildingOf[label]!) ?? [];
        list.push(label);
        byBuilding.set(buildingOf[label]!, list);
      }
      for (const [building, sheets] of byBuilding) {
        if (sheets.length < 3) continue;
        const cluster = clusters.find(
          (c) => c.building === building && Math.max(Math.abs(c.sx / c.n - x), Math.abs(c.sy / c.n - y)) <= clusterCells,
        );
        if (cluster === undefined) {
          clusters.push({ sx: x, sy: y, n: 1, sheets: new Set(sheets), building });
        } else {
          cluster.sx += x;
          cluster.sy += y;
          cluster.n += 1;
          for (const sheet of sheets) cluster.sheets.add(sheet);
        }
      }
    }
  }
  // An irregular crossing leaves several clusters naming the same sheets a few feet apart. If
  // they were kept apart, each sheet would end its cuts at a different one.
  for (let merged = true; merged; ) {
    merged = false;
    for (let a = 0; a < clusters.length && !merged; a++) {
      for (let b = a + 1; b < clusters.length && !merged; b++) {
        const p = clusters[a]!;
        const q = clusters[b]!;
        if (p.building !== q.building) continue;
        const shared = [...p.sheets].filter((sheet) => q.sheets.has(sheet)).length;
        const apart = Math.hypot(p.sx / p.n - q.sx / q.n, p.sy / p.n - q.sy / q.n);
        if (shared < 3 || apart > mergeCells) continue;
        p.sx += q.sx;
        p.sy += q.sy;
        p.n += q.n;
        for (const sheet of q.sheets) p.sheets.add(sheet);
        clusters.splice(b, 1);
        merged = true;
      }
    }
  }
  // A block centre can sit inside one sheet's area. Each junction moves to the nearest corner
  // where two of its sheets touch, or one touches the outside, so no sheet loses area to it.
  const snap = (cx: number, cy: number, sheets: ReadonlySet<number>): CellPoint => {
    const cell = (x: number, y: number) => (x < 0 || y < 0 || x >= width || y >= height ? -1 : labels[y * width + x]!);
    for (let r = 0; r <= mergeCells; r++) {
      let best: CellPoint | null = null;
      let bestDistance = Infinity;
      for (let y = Math.round(cy) - r; y <= Math.round(cy) + r; y++) {
        for (let x = Math.round(cx) - r; x <= Math.round(cx) + r; x++) {
          if (Math.max(Math.abs(x - Math.round(cx)), Math.abs(y - Math.round(cy))) !== r) continue;
          const around = new Set([cell(x - 1, y - 1), cell(x, y - 1), cell(x - 1, y), cell(x, y)]);
          const mine = [...around].filter((label) => sheets.has(label)).length;
          if (mine < 1 || around.size < 2 || [...around].some((label) => label >= 0 && !sheets.has(label))) continue;
          const d = Math.hypot(x - cx, y - cy);
          if (d < bestDistance) {
            bestDistance = d;
            best = { x, y };
          }
        }
      }
      if (best !== null) return best;
    }
    return { x: cx, y: cy };
  };
  return clusters.map((c) => ({ point: snap(c.sx / c.n, c.sy / c.n, c.sheets), sheets: c.sheets }));
}

/** Splits a stub of the given length off each end of an open chain, where the end segment is longer. */
function withStubs(corners: readonly CellPoint[], stub: number, head = true, tail = true): CellPoint[] {
  if (corners.length < 2) return corners.slice();
  const toward = (from: CellPoint, to: CellPoint): CellPoint | null => {
    const span = Math.hypot(to.x - from.x, to.y - from.y);
    if (span <= stub) return null;
    return { x: from.x + ((to.x - from.x) * stub) / span, y: from.y + ((to.y - from.y) * stub) / span };
  };
  const out = corners.slice();
  const tailStub = tail ? toward(out[out.length - 1]!, out[out.length - 2]!) : null;
  if (tailStub !== null) out.splice(out.length - 1, 0, tailStub);
  const headStub = head ? toward(out[0]!, out[1]!) : null;
  if (headStub !== null) out.splice(1, 0, headStub);
  return out;
}

export interface SheetClip {
  readonly polygon: PlanPoint[];
  readonly matchlines: PlannedMatchline[];
}

/** One step along the loop, with what lies on its outside: a neighbouring sheet, or -1. */
interface Step {
  readonly from: CellPoint;
  readonly neighbour: number;
}

function unitSteps(loop: readonly CellPoint[]): CellPoint[] {
  const out: CellPoint[] = [];
  for (let i = 0; i < loop.length; i++) {
    const a = loop[i]!;
    const b = loop[(i + 1) % loop.length]!;
    const n = Math.abs(b.x - a.x) + Math.abs(b.y - a.y);
    const dx = Math.sign(b.x - a.x);
    const dy = Math.sign(b.y - a.y);
    for (let k = 0; k < n; k++) out.push({ x: a.x + dx * k, y: a.y + dy * k });
  }
  return out;
}

/** The cell on the left of a unit step, which is inside a counter-clockwise loop. */
function insideCell(from: CellPoint, to: CellPoint): CellPoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return {
    x: Math.min(from.x, to.x) + (dy > 0 ? -1 : 0),
    y: Math.min(from.y, to.y) + (dx < 0 ? -1 : 0),
  };
}

/** The cell on the right of a unit step, which is outside a counter-clockwise loop. */
function outsideCell(from: CellPoint, to: CellPoint): CellPoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return {
    x: Math.min(from.x, to.x) + (dy < 0 ? -1 : 0),
    y: Math.min(from.y, to.y) + (dx > 0 ? -1 : 0),
  };
}

function length(points: readonly PlanPoint[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
  }
  return total;
}

export function clipSheet(
  raster: Raster,
  labels: Int32Array,
  buildingOf: readonly number[],
  sheet: number,
  grown: Mask,
  loop: readonly CellPoint[],
  junctions: readonly Junction[],
  diagonals: DiagonalCells,
  options: ClipOptions,
): SheetClip {
  const { width, height } = raster.shape;
  const at = (x: number, y: number, mask: Mask | Int32Array, fallback: number) =>
    x < 0 || y < 0 || x >= width || y >= height ? fallback : mask[y * width + x]!;

  const vertices = unitSteps(loop);
  const steps: Step[] = vertices.map((from, i) => {
    const to = vertices[(i + 1) % vertices.length]!;
    const cell = outsideCell(from, to);
    const label = at(cell.x, cell.y, labels, -1);
    const neighbour = label >= 0 && label !== sheet && buildingOf[label] === buildingOf[sheet] ? label : -1;
    return { from, neighbour };
  });

  // Start on an outside step, so no cut wraps past the end of the array.
  const start = steps.findIndex((s) => s.neighbour === -1);
  if (start < 0) return { polygon: dropCollinear(vertices, true).map((p) => toModel(raster, p)), matchlines: [] };
  const ordered = [...steps.slice(start), ...steps.slice(0, start)];
  const n = ordered.length;

  // A cell deep inside the grown area: a chord through it would cut off part of this sheet.
  const deep = (x: number, y: number) =>
    at(x, y, grown, 0) === 1 &&
    at(x - 1, y, grown, 0) === 1 &&
    at(x + 1, y, grown, 0) === 1 &&
    at(x, y - 1, grown, 0) === 1 &&
    at(x, y + 1, grown, 0) === 1;
  // The loop as runs: stretches with the same thing outside them, a neighbour or nothing.
  interface Run {
    readonly neighbour: number;
    readonly verts: CellPoint[];
    readonly cut: boolean;
    readonly before: number;
    readonly after: number;
    start: Junction | null;
    end: Junction | null;
  }
  const runs: Run[] = [];
  for (let i = 0; i < n; ) {
    const neighbour = ordered[i]!.neighbour;
    let j = i;
    while (j < n && ordered[j]!.neighbour === neighbour) j++;
    const verts = [...ordered.slice(i, j).map((s) => s.from), j < n ? ordered[j]!.from : ordered[0]!.from];
    const cut = neighbour >= 0 && length(verts.map((p) => toModel(raster, p))) >= options.minLengthInches;
    runs.push({
      neighbour,
      verts,
      cut,
      before: ordered[(i - 1 + n) % n]!.neighbour,
      after: ordered[j % n]!.neighbour,
      start: null,
      end: null,
    });
    i = j;
  }

  // A cut that ends against another sheet rather than outside ends at the junction all the
  // sheets there share, so their outlines and matchlines meet at one point.
  const reachCells = options.junctionReachInches / raster.cell;
  const junctionFor = (p: CellPoint, other: number): Junction | null => {
    let best: Junction | null = null;
    let bestDistance = reachCells;
    for (const junction of junctions) {
      if (!junction.sheets.has(sheet) || !junction.sheets.has(other)) continue;
      const d = Math.hypot(junction.point.x - p.x, junction.point.y - p.y);
      if (d <= bestDistance) {
        bestDistance = d;
        best = junction;
      }
    }
    return best;
  };
  for (const run of runs) {
    if (!run.cut) continue;
    if (run.before !== -1) run.start = junctionFor(run.verts[0]!, run.neighbour);
    if (run.after !== -1) run.end = junctionFor(run.verts[run.verts.length - 1]!, run.neighbour);
  }

  const polygon: CellPoint[] = [];
  const matchlines: PlannedMatchline[] = [];
  runs.forEach((run, r) => {
    // A cut from a junction back to the same junction is the diagonal neighbour across it,
    // touching only at that point: nothing to draw.
    if (run.cut && run.start !== null && run.start === run.end) return;
    if (!run.cut) {
      // A sliver of a third sheet between two cuts that end at the same junction is the
      // junction itself; keeping it would put a spike in the outline.
      const previous = runs[(r - 1 + runs.length) % runs.length]!;
      const next = runs[(r + 1) % runs.length]!;
      if (run.neighbour >= 0 && previous.end !== null && previous.end === next.start) return;
      polygon.push(...run.verts.slice(0, -1));
      return;
    }

    // A short stub at each outside end stays square: it crosses the outside wall, and the
    // labels sit on it. Between them the line is a rubber band: taut from end to end, kept out
    // of this sheet's own area and within the band's reach of the cut.
    const corners = dropCollinear(run.verts, false);
    if (run.start !== null) corners[0] = run.start.point;
    if (run.end !== null) corners[corners.length - 1] = run.end.point;
    const stubbed = withStubs(corners, options.stubInches / raster.cell, run.start === null, run.end === null);
    const from = run.start !== null ? 0 : 1;
    const to = run.end !== null ? stubbed.length - 1 : stubbed.length - 2;
    let simplified = stubbed;
    if (to > from) {
      const reach = Math.max(1, Math.round(options.bandInches / raster.cell));
      const box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
      const around = [run.start, run.end]
        .filter((j): j is Junction => j !== null)
        .flatMap((j) => [
          { x: j.point.x - reachCells, y: j.point.y - reachCells },
          { x: j.point.x + reachCells, y: j.point.y + reachCells },
        ]);
      for (const p of [...run.verts, ...stubbed, ...around]) {
        box.minX = Math.min(box.minX, Math.floor(p.x) - reach - 2);
        box.minY = Math.min(box.minY, Math.floor(p.y) - reach - 2);
        box.maxX = Math.max(box.maxX, Math.ceil(p.x) + reach + 1);
        box.maxY = Math.max(box.maxY, Math.ceil(p.y) + reach + 1);
      }
      const bw = box.maxX - box.minX + 1;
      const band = new Uint8Array(bw * (box.maxY - box.minY + 1));
      for (let k = 0; k + 1 < run.verts.length; k++) {
        for (const cell of [insideCell(run.verts[k]!, run.verts[k + 1]!), outsideCell(run.verts[k]!, run.verts[k + 1]!)]) {
          for (let dy = -reach; dy <= reach; dy++) {
            for (let dx = -reach; dx <= reach; dx++) {
              band[(cell.y + dy - box.minY) * bw + (cell.x + dx - box.minX)] = 1;
            }
          }
        }
      }
      // Near a junction the line has to reach a point inside the overlap, so there it may
      // cross the overlap, though never this sheet's own area.
      const near = [run.start, run.end].filter((j): j is Junction => j !== null).map((j) => j.point);
      const nearJunction = (x: number, y: number) =>
        near.some((p) => Math.max(Math.abs(x + 0.5 - p.x), Math.abs(y + 0.5 - p.y)) <= reachCells) &&
        at(x, y, labels, -1) !== sheet;
      const allowed = (x: number, y: number) =>
        x >= box.minX && y >= box.minY && x <= box.maxX && y <= box.maxY &&
        ((band[(y - box.minY) * bw + (x - box.minX)] === 1 && !deep(x, y)) || nearJunction(x, y));
      const traced = traceAlongWalls(
        stubbed[from]!,
        stubbed[to]!,
        allowed,
        box,
        diagonals,
        options.bendInches / raster.cell,
      );
      if (traced !== null) simplified = [...stubbed.slice(0, from), ...traced, ...stubbed.slice(to + 1)];
    }
    const straight = dropCollinear(simplified, false);
    polygon.push(...straight.slice(0, -1));

    const points = straight.map((p) => toModel(raster, p));
    const ends = [
      { index: 0, toward: 1, outside: run.before === -1, entering: true },
      { index: points.length - 1, toward: points.length - 2, outside: run.after === -1, entering: false },
    ];
    const neighbour = run.neighbour;
    const line = points.slice();
    const lineLabels: MatchlineLabel[] = [];
    for (const end of ends) {
      if (!end.outside) continue;
      const tipBase = points[end.index]!;
      const inner = points[end.toward]!;
      const reach = Math.hypot(tipBase.x - inner.x, tipBase.y - inner.y);
      const d = { x: Math.round((tipBase.x - inner.x) / reach), y: Math.round((tipBase.y - inner.y) / reach) };
      if (Math.abs(d.x) + Math.abs(d.y) !== 1) continue;
      const tip = { x: tipBase.x + d.x * options.extensionInches, y: tipBase.y + d.y * options.extensionInches };
      line[end.index] = tip;

      // The loop runs counter-clockwise, so this sheet is on the left of its direction of travel.
      const travel = end.entering ? { x: -d.x, y: -d.y } : d;
      const ownSide = { x: -travel.y, y: travel.x };
      const justify = (d.y === 0 ? d.x : d.y) > 0 ? "right" : "left";
      const offset = options.labelOffsetInches;
      const own = sheet + 1;
      const other = neighbour + 1;
      if (d.y === 0) {
        const ownAbove = ownSide.y > 0;
        lineLabels.push(
          { sheet: ownAbove ? own : other, position: { x: tip.x, y: tipBase.y + offset }, rotationDegrees: 0, horizontal: justify, vertical: "bottom" },
          { sheet: ownAbove ? other : own, position: { x: tip.x, y: tipBase.y - offset }, rotationDegrees: 0, horizontal: justify, vertical: "top" },
        );
      } else {
        // Rotated a quarter turn, the text's "up" points to -x.
        const ownUp = ownSide.x < 0;
        lineLabels.push(
          { sheet: ownUp ? own : other, position: { x: tipBase.x - offset, y: tip.y }, rotationDegrees: 90, horizontal: justify, vertical: "bottom" },
          { sheet: ownUp ? other : own, position: { x: tipBase.x + offset, y: tip.y }, rotationDegrees: 90, horizontal: justify, vertical: "top" },
        );
      }
    }
    matchlines.push({ sheet: sheet + 1, neighbour: neighbour + 1, points: line, labels: lineLabels });
  });

  const distinct = polygon.filter((p, k) => {
    const previous = polygon[(k - 1 + polygon.length) % polygon.length]!;
    return polygon.length === 1 || p.x !== previous.x || p.y !== previous.y;
  });
  return { polygon: dropCollinear(distinct, true).map((p) => toModel(raster, p)), matchlines };
}

